import { Checkbox, Dialog } from "construct-ui";
import * as bootstrap from 'bootstrap';

import PopupManagementModel from "../../models/pop-up-management/pop-up-management.model";
import languageModel from "../../models/language.model";
import noticeHistoryStorage from "../../storage/notice-history.storage";
import loginModel from "../../models/login.model";
import FileModel from "../../models/common/file.model"

/**
 * Pop up Notice
 * 
 * This component is for admin broadcast an announcement for web user.
 * 
 * @author Molly Liu
 */

let lastShownSession;

export default function PopupNoticeComponent() {

  let carousel; // Bootstrap carousel instance
  let noticeIndex = 0;
  let activeNoticeList = [];  // Notices to be displayed
  let noticeHistory = [];     // Disabled notices
  let noticeIdsToHide = [];   // List of notice IDs to hide

  const hasShownInCurrentSession = () => {
    return lastShownSession === loginModel.currentSession;
  }

  const getActiveNotices = () => {
    if (hasShownInCurrentSession()) return;

    PopupManagementModel.getPopupNotices()
      .then((data) => {
        if (!data) {
          activeNoticeList = [];
          return;
        }

        noticeHistory = noticeHistoryStorage.getNotices(loginModel.credentials.userID);

        // To exclude notices exist in client local history (previously marked to hide)
        activeNoticeList = data.filter(notice => {
          return noticeHistory.findIndex(history => history.popupId === notice.popupId) === -1
        })
      })
  }

  const hasActiveNotice = () => {
    return activeNoticeList.length > 0;
  }

  const hasMoreThanOneActiveNotice = () => {
    return activeNoticeList.length > 1;
  }

  const isShowingNotice = () => {
    return hasActiveNotice() && !hasShownInCurrentSession();
  }

  const handleDownload = (fileId, fileName) => {
    if (fileId && fileName) {
      FileModel.downloadByFileId(fileId, fileName)
        .then((result) => {
          // console.log(result)
        })
    }
  }

  const NoticeContent = () => {
    return {
      view: (vnode) => {
        const activeNotice = vnode.attrs.activeNotice; // active notice or {}
        const fileList = activeNotice.fileList || [];
        const imageFileList = fileList.length > 0 ? fileList.filter((f) => f.isImageFile) : [];
        const popupId = activeNotice.popupId;
        if (popupId) {
          return m('.notice-container.d-flex.flex-column.gap-4', { style: { 'width': '100%' } },
            m('.flex-grow-1',
              activeNotice.title &&
              m('h4.notice-title.fs-6.mb-3', activeNotice.title),
              m('.notice-content.mb-3',
                // m.trust(activeNotice.contents), // FUTURE Enhancement: JSON/WYSIWYG format?
                { style: "white-space: pre-line;" },
                activeNotice.contents,
              ),
              imageFileList.length !== 0 &&
              [
                m('.carousel.carousel-dark.slide.mb-2', { oncreate: initializeCarousel, id: `${popupId}-carousel` },
                  m('.carousel-indicators',
                    Array.from(imageFileList).map((_, index) => [
                      m('button', {
                        key: _.fileId,
                        class: index === 0 ? 'active' : '',
                        type: 'button',
                        'data-bs-target': `#${CSS.escape(popupId)}-carousel`,
                        'data-bs-slide-to': index,
                        'aria-label': `Image ${index + 1}`
                      })
                    ])
                  ),
                  m('.carousel-inner',
                    Array.from(imageFileList).map((image, index) => {
                      return m('.carousel-item', {
                        key: image.fileId,
                        class: index === 0 ? 'active' : '',
                        'data-index': index,
                      },
                        m('img', { src: image.base64Image, style: "width:100%" }),
                      );
                    })
                  ),
                  imageFileList.length > 1 && m('button.carousel-control-prev', { type: 'button', 'data-bs-target': `#${CSS.escape(popupId)}-carousel`, 'data-bs-slide': 'prev' },
                    m('span.carousel-control-prev-icon'),
                    m('.visually-hidden', 'Previous')
                  ),
                  imageFileList.length > 1 && m('button.carousel-control-next', { type: 'button', 'data-bs-target': `#${CSS.escape(popupId)}-carousel`, 'data-bs-slide': 'next' },
                    m('span.carousel-control-next-icon'),
                    m('.visually-hidden', 'Next')
                  ),
                )
              ]
            ),
            fileList.length > 0 && m('.row',
              m('.row',
                fileList.map(file =>
                  [
                    m('.col-md-6.mb-2.d-flex.justify-content-between',
                      m('a', {
                        href: '#',
                        onclick: (e) => {
                          e.preventDefault();
                          handleDownload(file.fileId, file.fileName);
                        }
                      }, file?.fileName || 'N/A'))
                  ])
              )
            ),
            m('.notice-actions',
              m(Checkbox, {
                checked: isHideNoticeChecked(),
                label: languageModel.getText('LT00000766', 'Do not show this notice again'),
                onchange: hideNoticeChecked
              })
            )
          )
        } else {
          return m('div')
        }
      }
    }
  }

  const isHideNoticeChecked = () => {
    return noticeIdsToHide.includes(activeNoticeList[noticeIndex]?.popupId);
  }

  const hideNoticeChecked = () => {
    if (noticeIdsToHide.includes(activeNoticeList[noticeIndex]?.popupId)) {
      noticeIdsToHide = noticeIdsToHide.filter(id => id !== activeNoticeList[noticeIndex].popupId);
    } else {
      noticeIdsToHide.push(activeNoticeList[noticeIndex].popupId)
    }
  }

  const prevNotice = () => {
    if (activeNoticeList.length > 0 && noticeIndex > 0) {
      noticeIndex--;
    }
  }

  const nextNotice = () => {
    if (noticeIndex < activeNoticeList.length - 1) {
      noticeIndex++;
    }
  }

  const close = () => {
    lastShownSession = loginModel.currentSession; // So it will not show again in current session
    let currentTimestamp = Date.now();
    // Update noticeHistoryStorage
    noticeHistoryStorage.addNotices(
      activeNoticeList
        .filter(n => noticeIdsToHide.includes(n.popupId))
        .map(notice => ({
          'userId': loginModel.credentials.userID,
          'popupId': notice.popupId,
          'timestamp': currentTimestamp,
        }))
    );
  }

  const initializeCarousel = ({ attrs: { id } }) => {
    carousel = new bootstrap.Carousel(`#${CSS.escape(id)}`);
  }

  return {
    oninit: () => {
      getActiveNotices();
    },
    view: () => {
      return m(Dialog, {
        class: 'cui-dialog-xl',
        closeOnOutsideClick: false,
        isOpen: isShowingNotice(),
        onClose: close,
        title: languageModel.getText('LT00000765', 'Notice'),
        content: hasActiveNotice() && m(NoticeContent, {
          activeNotice: activeNoticeList[noticeIndex] || {}
        }),
        footer: [
          m('.d-flex.justify-content-end.align-items-center',
            m('.d-flex.gap-3',
              hasMoreThanOneActiveNotice() &&
              m('.btn-group[role="group"]',
                m('button.btn.btn-white[type="button"]', {
                  disabled: noticeIndex === 0 ? true : false,
                  onclick: prevNotice,
                },
                  m('i.bi.bi-chevron-compact-left.me-sm-3.me-0'),
                  m('span.d-none.d-sm-inline-block', languageModel.getText('LT00000767', 'Prev')),
                ),
                m('button.btn.btn-white[type="button"]', {
                  disabled: noticeIndex === activeNoticeList.length - 1 ? true : false,
                  onclick: nextNotice
                },
                  m('span.d-none.d-sm-inline-block', languageModel.getText('LT00000768', 'Next')),
                  m('i.bi.bi-chevron-compact-right.ms-sm-3.me-0')
                ),
              ),
              m('button.btn.btn-white[type="button"]', {
                onclick: close
              }, languageModel.getText('LT00000021', 'Close'))
            ),
          )
        ]
      })
    }
  }
}